import React, { useEffect, useRef, useState } from "react";
import { pdf } from "@react-pdf/renderer";
import DocumentHeader from "./DocumentHeader";
import useDocumentList from "../../hooks/useDocumentList";
import InvoiceDataGrid from "./InvoiceDataGrid";
import InvoiceActivityLogs from "./InvoiceActivityLogs";
import QuotationPDF_2_cubix from "./SalesQuotationPdf";

const QuotationList = () => {
  const [p1Value, setP1Value] = useState("-");
  const [qtodayData, setQtodayData] = useState(null);
  const [qcmonthData, setQcmonthData] = useState(null);
  const [qbmonthData, setQbmonthData] = useState(null);
  const [modalType, setModalType] = useState(null);

  const {
    config,
    searchTerm,
    setSearchTerm,
    searchType,
    setSearchType,
    showSidebar,
    setShowSidebar,
    columnVisibilityModel,
    setColumnVisibilityModel,
    documentData,
    columns,
    rows,
    selectionModel,
    setSelectionModel,
    selectedRowData,
    setSelectedRowData,
    loading,
    handleEdit,
    exportDocumentList,
    handleView,
    pageNumber,
    setPageNumber,
    currentEnd,
    currentStart,
    totalCount,
    refreshDocumentList,
  } = useDocumentList({
    docType: "QUOT",
    docLabel: "Quotations",
    p1Value,
    p2Value: "-",
    docNoField: "QUOT NO",
    editRoute: "/quotation_entry",
    screenCode: "QOCOUNT",
    initialSearchType: "-",
  });

  const handleExport = () => {
    exportDocumentList();
  };

  const handleQuotationPrint = async (rowData) => {
    const quotNo = rowData?.["QUOT NO"];
    if (!quotNo) {
      alert("Please select a quotation to print");
      return;
    }

    const apiPaths = JSON.parse(localStorage.getItem("apiPaths") || "[]");
    const API_CONFIG = apiPaths[0]?.API_CONFIG || config.BASE_URL;
    const companyCode = localStorage.getItem("companyCode") || config.COMPANY_CODE;
    const department = localStorage.getItem("currentSelectedDepartment") || config.DEPARTMENT_NO;
    const currentUserObj = JSON.parse(localStorage.getItem("currentUser") || "[]");
    const dynamicVatFromAPI = currentUserObj[0]?.vatp || 0;

    try {
      const url = `${API_CONFIG}/api/DocumentEditDetails/${companyCode}/QUOT/${quotNo}/${department}`;
      // https://cubixweberp.com:213/api/DocumentEditDetails/SUNRISEWEB/QUOT/453880/MAIN
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch quotation data: ${response.statusText}`);
      }

      const rawData = await response.json();
      console.log("Raw Data print", rawData)
      if (!rawData || rawData.length === 0) {
        alert("No data available to print");
        return;
      }

      const productsInQuotationFromAPI = rawData.map((item, index) => ({
        id: new Date().getTime() + index,
        isSelectedItemCodeCorrect: true,
        itemCode: item.so_icode,
        itemDescription: item.idesc,
        itemUnit: item.unit,
        itemQuantity: item.tr_qty2,
        itemPrice: item.so_fccost,
        itemAmount: item.tr_qty2 * item.so_fccost,
        itemCurrentStock: "",
        itemImage: item.Imagepath,
        itemImageBase64: item.Imagebase64,
        itemWebLink: item.WebLink,
        area_code: item.area_code,
        quotation_number: item.so_no,
        revision_number: item.jv_num,
        foot1: item.w,
        itemCustRef: item.Cust_Ref ? item.Cust_Ref : item.size,
        Have_Sample: item.Have_Sample,
        head1: item.head1,
        sample_quantity_requested: item.Sample_Qty,
        itemFoc: item.so_fccost === 0 ? "FOC" : "",
        so_disc: item.so_disc,
        so_doc: item.lead_campaignid,
        payment: item.terms,
        salespersonName: item.salespersonName,
        Lead_Phone: item.phone ?? "",
        Lead_email: item.email ?? "",
        lead_CoordinatorName: item.lead_CoordinatorName,
        Coordinator: item.Coordinator,
        customer_name_from_details: item.accdesc,
        customer_account_from_details: item.cust_acc,
        project_name_from_details: item.insurance,
        soUid1: item.soUid1,
        fc: item.fc,
        dynamicVatFromAPI: dynamicVatFromAPI,
        Address: item.Address,
        Sales_Person: item.Sales_Person || item.sale_man,
        autoid: item.autoid,
        customer_email_from_details: item.email ?? "",
        customer_phone_from_details: item.phone ?? "",
      }));
      console.log("productsInQuotationFromAPI", productsInQuotationFromAPI)
      const selectedLead = {
        Salesorder_No: null,
        Proposal_No: quotNo,
        lead_email: rawData[0]?.email ?? "",
        lead_phone: rawData[0]?.phone ?? "",
      };

      const today = new Date();
      const formattedDate = `${String(today.getDate()).padStart(2, "0")}-${String(today.getMonth() + 1).padStart(2, "0")}-${today.getFullYear()}`;

      const vatValue = productsInQuotationFromAPI[0]?.foot1
        ? parseFloat(productsInQuotationFromAPI[0].foot1)
        : 0;

      const discountValue = rawData[0]?.so_disc || 0;
      const alreadySentQuotationNumber = quotNo;

      let preloadedImages = { header: null, footer: null };
      const rawCompanyCode = (localStorage.getItem("companyCode") || "").trim().toUpperCase();
      // if (rawCompanyCode === "SUNRISEWEB" || rawCompanyCode === "SUNRISEDXB") {
      //   try {
      //     const [headerRes, footerRes] = await Promise.all([
      //       fetch("/data/sunrisedxb_header.txt"),
      //       fetch("/data/sunrisedxb_footer.txt"),
      //     ]);
      //     const headerText = await headerRes.text();
      //     const footerText = await footerRes.text();
      //     preloadedImages.header = headerText.startsWith("data:image") ? headerText : `data:image/png;base64,${headerText}`;
      //     preloadedImages.footer = footerText.startsWith("data:image") ? footerText : `data:image/png;base64,${footerText}`;
      //   } catch (imgErr) {
      //     console.error("Error pre-fetching Sunrise images:", imgErr);
      //   }
      // }

      const blob = await pdf(
        <QuotationPDF_2_cubix
          selectedLead={selectedLead}
          productsInQuotationFromAPI={productsInQuotationFromAPI}
          formattedDate={formattedDate}
          vatValue={vatValue}
          alreadySentQuotationNumber={alreadySentQuotationNumber}
          discountValue={discountValue}
          salesOrderYesOrNo={{ isthisSalesOrder: false }}
          preloadedImages={preloadedImages}
        />
      ).toBlob();

      const pdfUrl = URL.createObjectURL(blob);
      window.open(pdfUrl);
      console.log("Sales quot PDF url ", pdfUrl)

      // const logPayload = [
      //   {
      //     cmpcode: config.COMPANY_CODE,
      //     doc_no: quotNo,
      //     status: "printed",
      //     evaluationcomments: "Printed Quotation (Cubix PDF)",
      //     userid: config.CURRENT_USER,
      //     deptno: config.DEPARTMENT_NO,
      //     doctype: "QOPRINT",
      //   },
      // ];

      // await fetch(`${config.BASE_URL}/api/CRMDocLogReg`, {
      //   method: "POST",
      //   headers: { "Content-Type": "application/json" },
      //   body: JSON.stringify(logPayload),
      // });
    } catch (err) {
      console.error("Error printing quotation:", err);
      alert("Failed to generate quotation PDF. Please try again.");
    }
  };

  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const params = ["QTODAY", "QCMONTH", "QBMONTH"];

    const fetchData = async () => {
      try {
        const responses = await Promise.all(
          params.map((param) =>
            fetch(
              `${config.BASE_URL}/api/CpaysCount/${config.COMPANY_CODE}/${param}/${config.CURRENT_USER}/-`
            ).then((res) => {
              if (!res.ok) throw new Error(`Error fetching ${param}`);
              return res.json();
            })
          )
        );

        setQtodayData(responses[0][0]?.Column1 || 0);
        setQcmonthData(responses[1][0]?.Column1 || 0);
        setQbmonthData(responses[2][0]?.Column1 || 0);
      } catch (err) {
        console.error(err);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <DocumentHeader
        title="Quotations"
        documentType="QUOTATION"
        todayQuotationsCount={qtodayData || 0}
        monthQuotationsCount={qcmonthData || 0}
        billedQuotationsCount={qbmonthData}
        showSidebar={showSidebar}
        setShowSidebar={setShowSidebar}
        BASE_URL={config.BASE_URL}
        COMPANY_CODE={config.COMPANY_CODE}
        DEPARTMENT={config.DEPARTMENT_NO}
        searchTerm={searchTerm}
        searchType={searchType}
        setSearchTerm={setSearchTerm}
        setSearchType={setSearchType}
        filteredDocs={documentData}
        showType={p1Value}
        setShowType={setP1Value}
        total={totalCount}
        onExport={handleExport}
        config={config}
        selectedRowData={selectedRowData}
        doctype="QUOT NO"
        onQuotationPrint={handleQuotationPrint}
      />
      <div className="max-w-full mx-auto py-2 space-y-4">
        <div className="grid grid-cols-12 gap-4">
          {showSidebar && (
            <InvoiceActivityLogs
              invoiceId={selectedRowData["QUOT NO"]}
              COMPANY_CODE={config.COMPANY_CODE}
              BASE_URL={config.BASE_URL}
              modificationType={"QOTIMELINE"}
              printType={"QOTPRINTIMELINE"}
              DEPARTMENT={config.DEPARTMENT_NO}
            />
          )}
          <div
            className={`col-span-12 ${showSidebar ? "lg:col-span-9" : "lg:col-span-12"
              } transition-all duration-300`}
          >
            <InvoiceDataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              selectionModel={selectionModel}
              setSelectionModel={setSelectionModel}
              setSelectedRowData={setSelectedRowData}
              selectedRowData={selectedRowData}
              onEdit={handleEdit}
              onView={handleView}
              columnVisibilityModel={columnVisibilityModel}
              setColumnVisibilityModel={setColumnVisibilityModel}
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              currentStart={currentStart}
              currentEnd={currentEnd}
              totalCount={totalCount}
              showSidebar={showSidebar}
              docLabel={"QT"}
              enableF5Action={true}
              config={config}
              refreshDocumentList={refreshDocumentList}
              f5Mode={"QOSETTLEMENT"}
              modalType={"close"}
              setModalType={setModalType}
            />
          </div>
        </div>
      </div>{" "}
    </div>
  );
};

export default QuotationList;
